import { useEffect, useState } from "react";
import {
  roomHasIntegrationToken,
  type PremiseConfig,
} from "@shared/types/foyer";
import { migrateConfig } from "@shared/migrate-config";
import { defaultSchemeForPremise } from "@shared/schemes/manifests";
import SchemeRouter, { isSchemeKey } from "./schemes/SchemeRouter";
import BootScreen from "./components/BootScreen";
import { normaliseHome, type TvCatalogueHome } from "./hooks/useTvCatalogue";

/**
 * Live in-CMS preview target. The editor embeds this in an iframe
 * (`/?preview=1&cms=1`) and postMessages the draft config on every edit - so
 * the scheme re-renders instantly without a save or a server round-trip.
 *
 * Nothing is fetched: the preview is whatever the editor last sent — the draft
 * config, and the room's real catalogue, which the editor reads on the
 * preview's behalf because `/tv/*` takes a screen token and a preview has no
 * pairing.
 */
export default function CmsPreview() {
  const [config, setConfig] = useState<PremiseConfig | null>(null);
  /**
   * The previewed room's real catalogue, sent by the editor.
   *
   * 🚨 Normalised with `normaliseHome` — the SAME function a bound television
   * uses on `/tv/home`. The route deliberately answers in the TV's own wire
   * shape so this can be true: a second parser is a second answer, and the two
   * would drift the first time either side gained a field.
   *
   * `undefined` until it arrives means "not asked for, or still on the wire",
   * never "empty" — the contract the whole tile-visibility model rests on.
   */
  const [vod, setVod] = useState<TvCatalogueHome | undefined>();

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const data = e.data as {
        type?: string;
        config?: unknown;
        vod?: unknown;
      } | null;
      if (data?.type === "foyer-preview-config" && data.config) {
        try {
          setConfig(migrateConfig(data.config));
        } catch {
          /* ignore malformed payloads */
        }
      }
      if (data?.type === "foyer-preview-vod" && data.vod) {
        try {
          setVod(normaliseHome(data.vod));
        } catch {
          /* ignore malformed payloads */
        }
      }
    }
    window.addEventListener("message", onMessage);
    // Let the editor know we're mounted so it sends the current draft. The
    // inline preview embeds us in an iframe (parent); the "Open preview" popup
    // opens us as a top-level window (opener). Ack both.
    const ack = { type: "foyer-preview-ready" } as const;
    window.opener?.postMessage(ack, "*");
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(ack, "*");
    }
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (!config) return <BootScreen label="Preview" />;

  // No template chosen renders the segment default (Nord for a hotel), exactly
  // as the television does - so the CMS never previews something else.
  const schemeKey = config.scheme?.key ?? defaultSchemeForPremise(config);
  if (isSchemeKey(schemeKey)) {
    return (
      <div className="foyer-app-shell">
        <div className="foyer-app" data-testid="app">
          <SchemeRouter
            schemeKey={schemeKey}
            config={config}
            roomId="preview"
            // Anything keyed on a room is unresolvable under the synthetic
            // "preview" id. Hand the VilMer lookup a REAL room so the pane
            // shows what a resident would see rather than "not connected yet".
            // Narrow on purpose - `roomId` itself stays synthetic, so the
            // whiteboard and photo slideshow keep reading nothing, and no PHI
            // reaches the template editor.
            previewRoomId={config.rooms?.find(roomHasIntegrationToken)?.id}
            // The real rails and pages this premise resolves, so pressing TV
            // shows what a guest would see rather than an empty library.
            previewVod={vod}
          />
        </div>
      </div>
    );
  }
  return <BootScreen label="Pick a template to preview" />;
}
